/** One control, chosen by the manifest's declared type. */

import type { ParamSpec } from "./manifest";
import { labelFor } from "./manifest";
import { Field } from "./ui";

interface Props {
  spec: ParamSpec;
  value: unknown;
  error?: string;
  disabled?: boolean;
  secrets?: string[];
  onChange: (value: unknown) => void;
}

export function ParamField({ spec, value, error, disabled, secrets, onChange }: Props) {
  const id = `param-${spec.name}`;
  const label = labelFor(spec);
  const hint = spec.description;

  // Booleans read better as a checkbox with the description beside it than as a
  // labelled field with a lone box underneath.
  if (spec.type === "boolean") {
    return (
      <div className="field">
        <div className="checkbox-row">
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
          />
          <label htmlFor={id}>
            <span className="field__label">{label}</span>
            {hint && <div className="field__hint">{hint}</div>}
          </label>
        </div>
        {error && <div className="field__error">{error}</div>}
      </div>
    );
  }

  if (spec.type === "enum" && spec.multiple) {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Field label={label} hint={hint} error={error} required={spec.required}>
        <div className="stack" style={{ gap: 6 }}>
          {(spec.options ?? []).map((option) => (
            <div className="checkbox-row" key={option}>
              <input
                id={`${id}-${option}`}
                type="checkbox"
                checked={selected.includes(option)}
                disabled={disabled}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option]
                      : selected.filter((item) => item !== option),
                  )
                }
              />
              <label htmlFor={`${id}-${option}`}>{option}</label>
            </div>
          ))}
        </div>
      </Field>
    );
  }

  if (spec.type === "enum") {
    return (
      <Field label={label} hint={hint} error={error} required={spec.required} htmlFor={id}>
        <select
          id={id}
          className="select"
          value={String(value ?? "")}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose…</option>
          {(spec.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (spec.type === "secret_ref") {
    return (
      <Field
        label={label}
        hint={hint ?? "The project secret to pass to the script. Its value is never shown."}
        error={error}
        required={spec.required}
        htmlFor={id}
      >
        <select
          id={id}
          className="select"
          value={String(value ?? "")}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose a secret…</option>
          {(secrets ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  // Both enum cases are handled above, so a multi-valued field here is a text list.
  if (spec.type === "list" || spec.multiple) {
    const items = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Field
        label={label}
        hint={hint ?? "One per line."}
        error={error}
        required={spec.required}
        htmlFor={id}
      >
        <textarea
          id={id}
          className="textarea"
          style={{ minHeight: 80 }}
          value={items.join("\n")}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(event) =>
            onChange(
              event.target.value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean),
            )
          }
        />
      </Field>
    );
  }

  if (spec.type === "keyvalue") {
    const pairs = (value ?? {}) as Record<string, string>;
    const text = Object.entries(pairs)
      .map(([key, item]) => `${key}=${item}`)
      .join("\n");
    return (
      <Field
        label={label}
        hint={hint ?? "One key=value per line."}
        error={error}
        required={spec.required}
        htmlFor={id}
      >
        <textarea
          id={id}
          className="textarea"
          style={{ minHeight: 80 }}
          value={text}
          disabled={disabled}
          onChange={(event) => {
            const next: Record<string, string> = {};
            for (const line of event.target.value.split("\n")) {
              const index = line.indexOf("=");
              if (index <= 0) continue;
              next[line.slice(0, index).trim()] = line.slice(index + 1).trim();
            }
            onChange(next);
          }}
        />
      </Field>
    );
  }

  const inputType =
    spec.type === "date"
      ? "date"
      : spec.type === "datetime"
        ? "datetime-local"
        : spec.type === "number" || spec.type === "integer"
          ? "number"
          : "text";

  return (
    <Field label={label} hint={hint} error={error} required={spec.required} htmlFor={id}>
      <input
        id={id}
        className="input"
        type={inputType}
        value={String(value ?? "")}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        min={spec.min ?? undefined}
        max={spec.max ?? undefined}
        step={spec.step ?? (spec.type === "integer" ? 1 : undefined)}
        onChange={(event) => {
          const raw = event.target.value;
          if (spec.type === "number" || spec.type === "integer") {
            onChange(raw === "" ? "" : Number(raw));
          } else {
            onChange(raw);
          }
        }}
      />
    </Field>
  );
}
